import { ApolloClient, ApolloLink, InMemoryCache, Observable } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ApolloProvider } from "@apollo/client/react";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs"; // 👈 deep import
import type { ChildrenProps } from "helpers";
import type React from "react";
import { useAuth0 } from "./react-auth0-spa";

const { VITE_APOLLO_SERVER_URL } = (window as any).__ENV;

const ApolloContextProvider: React.FC<ChildrenProps> = ({ children }) => {
	const apolloUrl = (VITE_APOLLO_SERVER_URL || "notfound") + "/graphql";
	const { getTokenSilently } = useAuth0();

	const withToken = setContext(async () => {
		try {
			const token = await getTokenSilently();
			return { token };
		} catch {
			return {};
		}
	});

	const authMiddleware = new ApolloLink((operation, forward) => {
		const { token } = operation.getContext();

		if (!token) {
			return new Observable(observer => observer.complete());
		}

		operation.setContext(({ headers = {} }) => ({
			headers: {
				...headers,
				Authorization: `Bearer ${token}`,
				"Apollo-Require-Preflight": "true"
			}
		}));

		return forward(operation);
	});

	const uploadLink = new UploadHttpLink({
		uri: apolloUrl,
		fetchOptions: { credentials: "omit" } // you use Authorization header, not cookies
	});

	const link = ApolloLink.from([withToken, authMiddleware.concat(uploadLink)]);

	const cache = new InMemoryCache({
		typePolicies: {
			Person: { fields: { credentials: { merge: false } } }
		}
	});

	const client = new ApolloClient({ link, cache });
	return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloContextProvider;